import { maybe } from '@passionware/monads';
import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';
import type { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { adaptLegacyWorkspaceRoutingForLinks } from './adaptLegacyWorkspaceRoutingForLinks';

/**
 * Layer 3 — entity behavior: list → open this entity, public URLs, share / slug prefixes.
 *
 * Read `enterprise_workspace_id` (and `team_id` where applicable) from the entity record.
 * Do not use browsing scope or the `enterprise` feature flag here.
 *
 * Not for in-page chrome on an already-open detail URL (tabs, breadcrumbs, section
 * links) — those stay on the current URL via `useApp*Routing` (layer 2).
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */

export type WorkspaceRef = { id: number; slug: string };

export type TeamRef = { id: number; slug: string };

export type WorkspaceScopedEntity = {
  enterprise_workspace_id?: number | null;
};

export type TeamScopedEntity = WorkspaceScopedEntity & {
  enterprise_team_id?: number | null;
};

export function getSharedEnterpriseWorkspaceId(
  entities: WorkspaceScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const workspaceIds = entities.map(
    entity => entity.enterprise_workspace_id ?? null
  );
  const definedIds = [
    ...new Set(workspaceIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedWorkspaceId = definedIds[0]!;
  if (workspaceIds.some(id => id != null && id !== sharedWorkspaceId)) {
    return null;
  }

  if (workspaceIds.some(id => id === null)) {
    return null;
  }

  return sharedWorkspaceId;
}

export function resolveEntityWorkspaceSlug(
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
): string | undefined {
  if (entity.enterprise_workspace_id == null || !workspaces?.length) {
    return undefined;
  }

  return workspaces.find(
    workspace => workspace.id === entity.enterprise_workspace_id
  )?.slug;
}

export function buildEntityWorkspaceRoutes(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
) {
  const workspaceSlug = resolveEntityWorkspaceSlug(entity, workspaces);

  if (workspaceSlug) {
    return appRouting.org(organizationSlug).workspace(workspaceSlug);
  }

  return adaptLegacyWorkspaceRoutingForLinks(
    myRouting.forWorkspace(organizationSlug)
  );
}

export function getContactDetailPath(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined,
  contactId: string | number
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    entity,
    workspaces
  ).contacts.detail(contactId);
}

export function getCalloutDetailPath(
  organizationSlug: string,
  callout: WorkspaceScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    callout,
    workspaces
  ).callouts.detail(callout.id);
}

export function resolveEntityTeamSlug(
  entity: TeamScopedEntity,
  teams: TeamRef[] | undefined
): string | undefined {
  if (entity.enterprise_team_id == null || !teams?.length) {
    return undefined;
  }

  return teams.find(team => team.id === entity.enterprise_team_id)?.slug;
}

/**
 * Detail path for a production from **entity assignment** (layer 3).
 *
 * For **list → open this production** (and similar entry points). Do not use for
 * sibling tabs / breadcrumbs while already on a production detail URL — use
 * `useAppProductionsRouting` instead.
 *
 * Resolves `enterprise_workspace_id` / `enterprise_team_id` via `workspaces` / `teams`
 * catalogs — same contract as {@link getShortlistDetailPath}.
 * Team URL when both slugs resolve, else legacy. No workspace-without-team home.
 */
export function getProductionDetailPath(
  organizationSlug: string,
  production: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams?: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(production, workspaces);
  const teamSlug = resolveEntityTeamSlug(production, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .productions.detail(production.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .productions()
    .detail(production.id);
}

/**
 * Detail path for a shortlist from **entity assignment** (layer 3).
 *
 * For **list → open this shortlist** (and similar entry points). Do not use for
 * in-page chrome while already on a shortlist detail URL — use browsing-scope
 * routing (`useAppShortlistsRouting` / slug app scope) instead.
 *
 * Uses `shortlist.enterprise_workspace_id` + `enterprise_team_id` and resolves those
 * numeric ids to URL slugs via the `workspaces` / `teams` catalogs. Callers must pass
 * catalogs that can contain the entity’s workspace/team (org list → fan-out; team list →
 * that workspace’s teams). Until both slugs resolve, returns the legacy
 * `/app/{org}/shortlists/{id}` path — so prefer not rendering the link (or not fetching
 * rows) until those catalogs are success if a wrong/legacy href is unacceptable.
 *
 * Same shape as productions / callsheets (no workspace-without-team home).
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */
export function getShortlistDetailPath(
  organizationSlug: string,
  shortlist: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams?: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(shortlist, workspaces);
  const teamSlug = resolveEntityTeamSlug(shortlist, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .shortlists.detail(shortlist.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .shortlists()
    .detail(shortlist.id);
}

type TeamScopedDetailPathDeps = {
  workspaces?: WorkspaceRef[];
  enterpriseService: Pick<EnterpriseService, 'ensureWorkspace' | 'ensureTeam'>;
};

async function resolveEntityWorkspaceAndTeamSlugs(
  organizationId: string,
  entity: TeamScopedEntity,
  workspaces: WorkspaceRef[] | undefined,
  enterpriseService: TeamScopedDetailPathDeps['enterpriseService']
): Promise<{ workspaceSlug?: string; teamSlug?: string }> {
  let workspaceSlug = resolveEntityWorkspaceSlug(entity, workspaces);
  if (!workspaceSlug && entity.enterprise_workspace_id != null) {
    const workspace = await enterpriseService.ensureWorkspace(
      organizationId,
      entity.enterprise_workspace_id
    );
    workspaceSlug = maybe.flatMapOrElse(workspace, w => w.slug, undefined);
  }

  let teamSlug: string | undefined;
  if (
    entity.enterprise_team_id != null &&
    entity.enterprise_workspace_id != null
  ) {
    const team = await enterpriseService.ensureTeam(
      organizationId,
      entity.enterprise_workspace_id,
      entity.enterprise_team_id
    );
    teamSlug = maybe.flatMapOrElse(team, t => t.slug, undefined);
  }

  return { workspaceSlug, teamSlug };
}

/** Layer 3 detail path — resolves workspace/team slugs via enterprise service when refs are missing. */
export async function getShortlistDetailPathAsync(
  organizationSlug: string,
  shortlist: TeamScopedEntity & { id: number },
  deps: TeamScopedDetailPathDeps
): Promise<string> {
  const { workspaceSlug, teamSlug } = await resolveEntityWorkspaceAndTeamSlugs(
    organizationSlug,
    shortlist,
    deps.workspaces,
    deps.enterpriseService
  );

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .shortlists.detail(shortlist.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .shortlists()
    .detail(shortlist.id);
}

export function getSharedEnterpriseTeamId(
  entities: TeamScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const teamIds = entities.map(entity => entity.enterprise_team_id ?? null);
  const definedIds = [
    ...new Set(teamIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedTeamId = definedIds[0]!;
  if (teamIds.some(id => id != null && id !== sharedTeamId)) {
    return null;
  }

  if (teamIds.some(id => id === null)) {
    return null;
  }

  return sharedTeamId;
}
